import { useContext, useEffect, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Modal, Text } from '@calm-web/design-system';

import { MediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import Header from '@/components/header/Header';
import PaymentDetailsModal from '@/components/PreSignupFlow/Components/PaymentDetailsModal';
import { Cta } from '@/components/PreSignupFlow/Components/PaymentFormModule/styles';
import PaymentElementsForm from '@/components/purchase/PaymentElementsForm';
import PurchaseDetails from '@/components/purchase/PurchaseDetails/refreshedPurchaseDetails';
import PurchaseForm from '@/components/purchase/PurchaseForm';
import { useStripeElements } from '@/components/purchase/StripeElementsProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useSubPurchase } from '@/hooks/purchase/useSubPurchase';
import { useUserState } from '@/hooks/store/useAppState';
import { userCanTrial } from '@/utils/subscriptions';

import { ShareContext } from '../ShareProvider';
import messages from './messages';
import {
	Container,
	LimitedTimeCallout,
	PaymentSubHeading,
	PaymentTitleHeading,
	PaymentTitleWrapper,
	Wrapper,
} from './styles';

function SharePayment(): JSX.Element {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const submitSubPurchase = useSubPurchase();
	const user = useUserState();
	const { isStripeNextEnabled } = useStripeElements();
	const isFreeTrial = userCanTrial(user);
	const { playerMode, onPlay } = useContext(MediaPlayerContext);

	const { shouldShowSharePayment, onUpsellExit: _onUpsellExit, onPurchaseSuccess } = useContext(ShareContext);

	function onUpsellExit() {
		if (playerMode === 'paused') {
			onPlay();
		}
		logEvent({
			eventName: 'Screen : Exited',
			eventProps: {
				screen: 'Modal',
				type: 'Payment Screen',
			},
		});
		_onUpsellExit();
	}

	function onTooltipClick(): void {
		setIsModalOpen(true);
		logEvent({
			eventName: 'Subscribe : Purchase : Form : Tool Tip : Clicked',
			eventProps: {
				tool_tip: 'why now',
			},
		});
	}
	const messageHeading = isFreeTrial ? messages.trialHeading : messages.noTrialheading;
	const showCallout = isFreeTrial;

	useEffect(() => {
		if (shouldShowSharePayment) {
			logEvent({
				eventName: 'Screen : Viewed',
				eventProps: {
					screen: 'Modal',
					type: 'Payment Screen',
				},
			});
		}
	}, [shouldShowSharePayment, logEvent]);

	return (
		<Wrapper isVisible={shouldShowSharePayment}>
			<Header isStatic hideMenu hideProfile onExit={onUpsellExit} />
			<Container activeScreen={true}>
				{showCallout && (
					<div>
						<LimitedTimeCallout>{formatMessage(messages.todayOnly)}</LimitedTimeCallout>
					</div>
				)}
				<Text el="h1" styleAs="h4" color="gray7" weight={FontWeights.Medium}>
					{formatMessage(messageHeading, {
						br: <br />,
					})}
				</Text>
				<PaymentSubHeading size={FontSizes.base} color="gray7" weight={FontWeights.Regular}>
					{formatMessage(messages.subheading)}
				</PaymentSubHeading>
				<PurchaseDetails isShareContent={true} />
				<PaymentTitleWrapper>
					<PaymentTitleHeading styleAs="h4" color="gray7" weight={FontWeights.Regular}>
						{formatMessage(messages.heading)}
					</PaymentTitleHeading>
					{isFreeTrial && <Cta onClick={onTooltipClick}>{formatMessage(messages.modalCta)}</Cta>}
				</PaymentTitleWrapper>
				{shouldShowSharePayment && (
					<>
						{isStripeNextEnabled ? (
							<PaymentElementsForm endpoint="subscription" afterPurchaseSuccess={onPurchaseSuccess} />
						) : (
							<PurchaseForm onSuccess={onPurchaseSuccess} onPurchase={submitSubPurchase} />
						)}
					</>
				)}
				<Modal
					isOpen={isModalOpen}
					closeModal={(): void => setIsModalOpen(false)}
					canClose
					aria-label={formatMessage(messages.modalCta)}
				>
					<PaymentDetailsModal onCloseClick={(): void => setIsModalOpen(false)} />
				</Modal>
			</Container>
		</Wrapper>
	);
}

export default SharePayment;
